import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { BlockMath, InlineMath } from 'react-katex';
import { ArrowLeft, ArrowRight, CheckCircle2, XCircle, ChevronRight } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import SectionWrapper from '@/components/SectionWrapper';
import FormulaCard from '@/components/FormulaCard';
import TransformerTurnsRatioCalc from '@/calculators/TransformerTurnsRatioCalc';
import ImpedanceReferralCalc from '@/calculators/ImpedanceReferralCalc';
import DeltaWyePhaseShiftCalc from '@/calculators/DeltaWyePhaseShiftCalc';

// ─── Section 1: Why It Matters ────────────────────────────────────────────────
const WhyItMatters = () => (
  <SectionWrapper number="1" title="Why It Matters" id="why-it-matters">
    <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-lg p-5 mb-5">
      <p className="text-sm font-medium text-amber-800 dark:text-amber-300 mb-2">Real Utility Scenario</p>
      <p className="text-slate-700 dark:text-slate-300 leading-relaxed text-sm">
        A protection engineer must set the differential relay (87T) on a 30 MVA, 138/13.8 kV, Dyn11 transformer.
        Three things are immediately required:{' '}
        <strong className="text-slate-900 dark:text-white">(1)</strong> the turns ratio{' '}
        <InlineMath math="a = 138/13.8 = 10" /> to match CT ratios on both sides;{' '}
        <strong className="text-slate-900 dark:text-white">(2)</strong> the leakage impedance{' '}
        <InlineMath math="Z_T = 7\%" /> referred to the 138 kV side{' '}
        (<InlineMath math="Z_T = 0.07 \times (138^2/30) = 44.4\,\Omega" />);{' '}
        and <strong className="text-slate-900 dark:text-white">(3)</strong> the 30° phase shift
        introduced by the delta-wye connection, which must be compensated in the relay software
        or by using delta-connected CTs — otherwise the relay will see a false differential current
        and trip every time the transformer is energized.
      </p>
    </div>
    <p className="text-slate-600 dark:text-slate-400 leading-relaxed mb-3">
      Transformers are the <strong className="text-slate-800 dark:text-slate-200">interface between voltage levels</strong> in every power system.
      They convert the 765 kV bulk transmission voltage down through 345 kV, 138 kV, 69 kV, 13.8 kV, and finally to the
      240/120 V service entrance — each step requiring a transformer whose properties must be fully understood.
    </p>
    <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
      Beyond voltage conversion, transformers introduce two effects that affect <em>every</em> protection and fault study:
      the <strong className="text-slate-800 dark:text-slate-200">impedance referral</strong> (impedances scale by the square of the turns ratio —
      not the turns ratio itself) and the <strong className="text-slate-800 dark:text-slate-200">zero-sequence blocking</strong>
      provided by delta windings, which determines whether a ground fault on the secondary side can drive ground current
      on the primary side.
    </p>
  </SectionWrapper>
);

// ─── Section 2: Concept Visual ────────────────────────────────────────────────
const ConceptVisual = () => (
  <SectionWrapper number="2" title="Concept Visual" id="concept-visual" alt>
    <p className="text-slate-600 dark:text-slate-400 text-sm mb-5">
      The diagram shows how the HV (primary) phasors Va, Vb, Vc on the left relate to the LV (secondary)
      phasors Va′, Vb′, Vc′ on the right after passing through a Dyn11 transformer. The LV phasors are
      shifted <strong className="text-slate-800 dark:text-slate-200">+30° counterclockwise</strong> relative to the HV phasors.
    </p>
    <div className="overflow-x-auto">
      <svg viewBox="0 0 700 240" className="w-full max-w-3xl mx-auto" style={{ minWidth: 320 }}>

        {/* ── HV phasor diagram (left) ── */}
        <text x="120" y="16" fontSize="10.5" fill="#94a3b8" fontFamily="IBM Plex Mono" fontWeight="600" textAnchor="middle">HV PHASORS (138 kV)</text>
        {/* Grid circle */}
        <circle cx="120" cy="120" r="76" fill="none" stroke="#e2e8f0" strokeWidth="1" strokeDasharray="4 3" />
        <line x1="32" y1="120" x2="208" y2="120" stroke="#e2e8f0" strokeWidth="1" />
        <line x1="120" y1="40" x2="120" y2="200" stroke="#e2e8f0" strokeWidth="1" />
        {/* Va: 0° */}
        <line x1="120" y1="120" x2="189" y2="120" stroke="#0d9488" strokeWidth="2.5" />
        <polygon points="196,120 184,114.5 184,125.5" fill="#0d9488" />
        <text x="205" y="117" fontSize="11" fill="#0d9488" fontFamily="IBM Plex Mono" fontWeight="700">Va</text>
        <text x="205" y="129" fontSize="8.5" fill="#0d9488" fontFamily="IBM Plex Mono">∠0°</text>
        {/* Vb: -120° */}
        <line x1="120" y1="120" x2="82" y2="186" stroke="#6366f1" strokeWidth="2.5" />
        <polygon points="76,196 83,183 92,190" fill="#6366f1" />
        <text x="58" y="210" fontSize="11" fill="#6366f1" fontFamily="IBM Plex Mono" fontWeight="700">Vb ∠−120°</text>
        {/* Vc: +120° */}
        <line x1="120" y1="120" x2="82" y2="54" stroke="#f59e0b" strokeWidth="2.5" />
        <polygon points="76,44 83,57 92,50" fill="#f59e0b" />
        <text x="58" y="36" fontSize="11" fill="#f59e0b" fontFamily="IBM Plex Mono" fontWeight="700">Vc ∠+120°</text>

        {/* ── Center: Dyn11 label + arrow ── */}
        <rect x="268" y="100" width="120" height="40" rx="6" fill="#1e293b" stroke="#0d9488" strokeWidth="1.5" />
        <text x="328" y="117" fontSize="10.5" fill="#5eead4" fontFamily="IBM Plex Mono" fontWeight="700" textAnchor="middle">Dyn11</text>
        <text x="328" y="131" fontSize="9" fill="#94a3b8" fontFamily="IBM Plex Mono" textAnchor="middle">LV leads HV by +30°</text>
        <line x1="196" y1="120" x2="268" y2="120" stroke="#64748b" strokeWidth="1.5" strokeDasharray="4 2" markerEnd="url(#arr)" />
        <line x1="388" y1="120" x2="460" y2="120" stroke="#64748b" strokeWidth="1.5" strokeDasharray="4 2" />
        <polygon points="467,120 457,114.5 457,125.5" fill="#64748b" />

        {/* ── LV phasor diagram (right) — shifted +30° ── */}
        <text x="555" y="16" fontSize="10.5" fill="#94a3b8" fontFamily="IBM Plex Mono" fontWeight="600" textAnchor="middle">LV PHASORS (13.8 kV)</text>
        <circle cx="555" cy="120" r="76" fill="none" stroke="#e2e8f0" strokeWidth="1" strokeDasharray="4 3" />
        <line x1="467" y1="120" x2="643" y2="120" stroke="#e2e8f0" strokeWidth="1" />
        <line x1="555" y1="40" x2="555" y2="200" stroke="#e2e8f0" strokeWidth="1" />
        {/* Va': +30° */}
        <line x1="555" y1="120" x2="621" y2="82" stroke="#5eead4" strokeWidth="2.5" strokeDasharray="6 2" />
        <polygon points="627,78 617,79 621,89" fill="#5eead4" />
        <text x="632" y="78" fontSize="11" fill="#5eead4" fontFamily="IBM Plex Mono" fontWeight="700">Va′</text>
        <text x="632" y="90" fontSize="8.5" fill="#5eead4" fontFamily="IBM Plex Mono">∠+30°</text>
        {/* Vb': -90° */}
        <line x1="555" y1="120" x2="555" y2="194" stroke="#a5b4fc" strokeWidth="2.5" strokeDasharray="6 2" />
        <polygon points="555,200 549,188 561,188" fill="#a5b4fc" />
        <text x="558" y="215" fontSize="11" fill="#a5b4fc" fontFamily="IBM Plex Mono" fontWeight="700">Vb′ ∠−90°</text>
        {/* Vc': +150° */}
        <line x1="555" y1="120" x2="489" y2="82" stroke="#fcd34d" strokeWidth="2.5" strokeDasharray="6 2" />
        <polygon points="483,78 487,89 497,79" fill="#fcd34d" />
        <text x="442" y="70" fontSize="11" fill="#fcd34d" fontFamily="IBM Plex Mono" fontWeight="700">Vc′ ∠+150°</text>

        {/* +30° arc on LV diagram */}
        <path d="M 585 120 A 30 30 0 0 1 581 90" fill="none" stroke="#8b5cf6" strokeWidth="1.5" />
        <text x="592" y="106" fontSize="9" fill="#8b5cf6" fontFamily="IBM Plex Mono">+30°</text>
      </svg>
    </div>
    <p className="text-xs text-slate-500 dark:text-slate-500 text-center mt-2">
      Solid arrows = HV phasors (reference). Dashed arrows = LV phasors after Dyn11 +30° shift.
      Magnitudes differ by the turns ratio a = 10 (not shown to scale).
    </p>
  </SectionWrapper>
);

// ─── Section 3: Core Theory ───────────────────────────────────────────────────
const CoreTheory = () => (
  <SectionWrapper number="3" title="Core Theory" id="core-theory">
    <div className="space-y-7">

      <div>
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2 font-heading">A. Turns Ratio and the Ideal Transformer</h3>
        <p className="text-slate-600 dark:text-slate-400 leading-relaxed text-sm mb-2">
          An ideal transformer conserves power (<InlineMath math="V_1 I_1 = V_2 I_2" />) with voltages scaling by the
          turns ratio <InlineMath math="a = N_1/N_2 = V_1/V_2" /> and currents scaling inversely{' '}
          (<InlineMath math="I_1/I_2 = N_2/N_1 = 1/a" />). High-voltage windings have more turns and proportionally less current;
          low-voltage windings have fewer turns and more current. Impedances, however, transform by{' '}
          <strong className="text-slate-800 dark:text-slate-200">the square of the turns ratio: <InlineMath math="Z_{pri} = Z_{sec} \times a^2" /></strong>.
        </p>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2 font-heading">B. Transformer Leakage Impedance</h3>
        <p className="text-slate-600 dark:text-slate-400 leading-relaxed text-sm mb-2">
          Real transformers have leakage reactance <InlineMath math="X_T" /> (flux that does not link both windings)
          and winding resistance <InlineMath math="R_T" /> (copper losses). On transformer nameplates,{' '}
          <strong className="text-slate-800 dark:text-slate-200">%Z</strong> (percent impedance) expresses this
          total leakage as a percentage of the base impedance:{' '}
          <InlineMath math="Z_T(\Omega) = \frac{\%Z}{100} \times \frac{V^2_{\text{rated}}}{S_{\text{rated}}}" />.
          In per-unit: <InlineMath math="Z_T(\text{pu}) = \%Z / 100" />.
          Typical values: 5–8% for power transformers, 2–4% for distribution transformers.
        </p>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2 font-heading">C. Why Delta Blocks Zero-Sequence Current</h3>
        <div className="bg-indigo-50 dark:bg-indigo-900/10 border border-indigo-200 dark:border-indigo-800 rounded-lg p-4 mb-3">
          <p className="text-sm font-semibold text-indigo-900 dark:text-indigo-200 mb-2">Critical concept for sequence networks and protection</p>
          <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed mb-3">
            Zero-sequence currents (<InlineMath math="I_0" />) are equal in magnitude and identical in phase angle
            in all three conductors simultaneously. For zero-sequence current to flow, it must have a return path through
            a neutral connection. A delta winding has <em>no neutral point</em> — the three winding voltages form
            a closed loop.
          </p>
          <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed mb-2">
            What actually happens: zero-sequence current <em>can</em> circulate <strong className="text-slate-800 dark:text-slate-200">inside</strong> the
            delta loop (driven by the MMF from the wye side), but it <strong className="text-slate-800 dark:text-slate-200">cannot flow in the external line conductors</strong> connected
            to the delta terminals. By KCL, the three line currents at each delta node must sum to zero; but three equal
            zero-sequence currents all in the same direction cannot satisfy this — so <InlineMath math="I_0 = 0" /> in
            the external system connected to a delta terminal.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
            {[
              { label: 'Wye with neutral (grounded)', detail: 'Zero-sequence CAN flow. Ground fault current returns through the neutral. Used for grounding purposes.', color: 'teal' },
              { label: 'Delta winding', detail: 'Zero-sequence BLOCKED in line conductors. Ground fault on delta side cannot drive ground current on the system connected to delta terminals.', color: 'amber' },
            ].map(({ label, detail, color }) => (
              <div key={label} className={`rounded-md p-3 border ${color === 'teal' ? 'bg-teal-50/60 dark:bg-teal-900/10 border-teal-200 dark:border-teal-800' : 'bg-amber-50/60 dark:bg-amber-900/10 border-amber-200 dark:border-amber-800'}`}>
                <p className={`text-xs font-semibold mb-1 ${color === 'teal' ? 'text-teal-700 dark:text-teal-300' : 'text-amber-700 dark:text-amber-300'}`}>{label}</p>
                <p className="text-xs text-slate-600 dark:text-slate-400">{detail}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2 font-heading">D. Phase Shift in Delta-Wye Connections</h3>
        <p className="text-slate-600 dark:text-slate-400 leading-relaxed text-sm mb-3">
          A delta-wye transformer introduces a <strong className="text-slate-800 dark:text-slate-200">fixed phase shift</strong> between
          HV and LV winding voltages. This arises geometrically: the wye winding voltage is a phasor
          difference of the delta winding currents. The magnitude of the shift is always 30° — only the
          direction (leading or lagging) depends on the winding polarity and physical arrangement,
          encoded in the vector group number.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-700">
                <th className="py-2 px-3 text-left font-medium text-slate-500 dark:text-slate-500">Vector Group</th>
                <th className="py-2 px-3 text-left font-medium text-slate-500 dark:text-slate-500">Connections</th>
                <th className="py-2 px-3 text-left font-medium text-slate-500 dark:text-slate-500">Phase Shift</th>
                <th className="py-2 px-3 text-left font-medium text-slate-500 dark:text-slate-500">Zero-Seq Blocking</th>
                <th className="py-2 px-3 text-left font-medium text-slate-500 dark:text-slate-500">Common Use</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-xs">
              {[
                ['Dyn11', 'D HV / yn LV', 'LV leads HV by +30°', 'HV side (delta)', 'UK/EU distribution substations'],
                ['Dyn1',  'D HV / yn LV', 'LV lags HV by −30°',  'HV side (delta)', 'North American substations'],
                ['YNyn0', 'YN HV / yn LV','No phase shift (0°)',  'Neither side',    'Autotransformers, grounding xfmrs'],
                ['YNd11', 'YN HV / d LV', 'LV leads HV by +30°', 'LV side (delta)', 'Generator step-up transformers'],
              ].map(([vg, conn, shift, block, use]) => (
                <tr key={vg} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                  <td className="py-2 px-3 font-mono font-bold text-teal-700 dark:text-teal-400">{vg}</td>
                  <td className="py-2 px-3 font-mono text-slate-700 dark:text-slate-300">{conn}</td>
                  <td className="py-2 px-3 text-slate-700 dark:text-slate-300">{shift}</td>
                  <td className="py-2 px-3 text-slate-500 dark:text-slate-400">{block}</td>
                  <td className="py-2 px-3 text-slate-500 dark:text-slate-400">{use}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2 font-heading">E. Vector Group Notation Decoded</h3>
        <div className="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
          <p className="text-sm font-semibold text-slate-900 dark:text-white mb-3 font-heading">Reading "Dyn11":</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { char: 'D', name: 'HV winding', detail: 'Delta connected (uppercase = HV side)' },
              { char: 'y', name: 'LV winding', detail: 'Wye connected (lowercase = LV side)' },
              { char: 'n', name: 'Neutral', detail: 'Neutral terminal brought out on LV side' },
              { char: '11', name: 'Clock position', detail: '11 × 30° = 330° ≡ −30° lag ≡ +30° lead. LV leads HV by 30°.' },
            ].map(({ char, name, detail }) => (
              <div key={char} className="text-center">
                <div className="text-2xl font-mono font-bold text-teal-700 dark:text-teal-400 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-lg py-2 mb-2">{char}</div>
                <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1 font-heading">{name}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">{detail}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

    </div>
  </SectionWrapper>
);

// ─── Section 4: Formula Sheet ─────────────────────────────────────────────────
const FormulaSheet = () => (
  <SectionWrapper number="4" title="Formula Sheet" id="formulas" alt>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <FormulaCard
        title="Turns Ratio (IEEE C57)"
        formula={"a = \\frac{N_1}{N_2} = \\frac{V_1}{V_2} = \\frac{I_2}{I_1}"}
        variables={[
          { sym: 'a',  desc: 'Turns ratio (dimensionless) — defined as HV/LV, always ≥ 1 for step-down' },
          { sym: 'N₁', desc: 'Number of primary (HV) winding turns' },
          { sym: 'N₂', desc: 'Number of secondary (LV) winding turns' },
          { sym: 'V₁', desc: 'Rated HV line-to-line voltage (V or kV)' },
          { sym: 'I₁', desc: 'HV line current (A); note: I₁ = I₂/a, current inverts from voltage' },
        ]}
        note="Example: 138/13.8 kV → a = 10. Full-load I₂ = 1255 A → I₁ = 1255/10 = 125.5 A."
      />
      <FormulaCard
        title="Impedance Referral (IEEE C57.12)"
        formula={"Z_{\\text{pri}} = Z_{\\text{sec}} \\times a^2 \\qquad Z_{\\text{sec}} = Z_{\\text{pri}} \\times \\frac{1}{a^2}"}
        variables={[
          { sym: 'Z_pri', desc: 'Impedance referred to (or located on) the primary (HV) side (Ω)' },
          { sym: 'Z_sec', desc: 'Impedance referred to (or located on) the secondary (LV) side (Ω)' },
          { sym: 'a²',    desc: 'Square of the turns ratio — NOT a. This is the most common error.' },
        ]}
        note="Example: Z_sec = 0.5Ω, a = 10 → Z_pri = 0.5 × 100 = 50 Ω. The impedance grows by a² = 100× when moved to HV side."
      />
      <FormulaCard
        title="Leakage Impedance from Nameplate %Z"
        formula={"Z_T(\\Omega) = \\frac{\\%Z}{100} \\times \\frac{V^2_{\\text{rated}}}{S_{\\text{rated}}}"}
        variables={[
          { sym: '%Z', desc: 'Nameplate percent impedance (dimensionless, e.g. 7 for 7%)' },
          { sym: 'V_rated', desc: 'Rated voltage on the side you want the impedance on (V or kV)' },
          { sym: 'S_rated', desc: 'Transformer MVA rating (VA or MVA, must match V² units)' },
        ]}
        note="Example: 30 MVA, 138 kV, 7%Z → Z_T = 0.07 × (138²/30) = 0.07 × 634.8 = 44.44 Ω (primary). Secondary: 0.07 × (13.8²/30) = 0.4444 Ω ✓"
      />
      <FormulaCard
        title="Per-Unit Transformer Impedance"
        formula={"Z_{T}(\\text{pu}) = \\frac{\\%Z}{100} \\text{ (same on both sides of transformer)}"}
        variables={[
          { sym: 'Z_T(pu)', desc: 'Transformer impedance in per-unit — identical on primary and secondary when using rated base' },
          { sym: '%Z',      desc: 'Nameplate percent impedance (same value regardless of which side you use as base)' },
        ]}
        note="This is the entire advantage of per-unit: no a² referral needed. 7% Z_T on primary base = 7% on secondary base. The turns ratio disappears inside the pu normalization."
      />
      <FormulaCard
        title="Phase Shift — Dyn11 (LV leads HV)"
        formula={"\\theta_{\\text{LV}} = \\theta_{\\text{HV}} + 30^\\circ \\quad(\\text{Dyn11, positive sequence})"}
        variables={[
          { sym: 'θ_LV', desc: 'Phase angle of LV side voltage phasors (degrees)' },
          { sym: 'θ_HV', desc: 'Phase angle of HV side voltage phasors (degrees, reference)' },
          { sym: '+30°',  desc: 'LV phasors lead HV by 30° for Dyn11. Use −30° for Dyn1.' },
        ]}
        note="Example: HV Va = 79.67 kV ∠0°. LV Va′ = 7.967 kV ∠+30° (Dyn11). For Dyn1: Va′ = 7.967 kV ∠−30°."
      />
      <FormulaCard
        title="Zero-Sequence Equivalent — Delta Side"
        formula={"I_{0,\\text{line}} = 0 \\text{ (external to delta)} \\quad I_{0,\\Delta} \\text{ circulates inside}"}
        variables={[
          { sym: 'I_0,line', desc: 'Zero-sequence current in line conductors connected to a delta — always zero' },
          { sym: 'I_0,Δ',    desc: 'Zero-sequence current circulating inside the delta loop (driven by MMF from other winding)' },
        ]}
        note="In sequence network diagrams: a delta winding appears as an open circuit in the zero-sequence network on the line-conductor side — it disconnects the zero-sequence network."
      />
    </div>
  </SectionWrapper>
);

// ─── Section 5: Common Mistakes ───────────────────────────────────────────────
const MISTAKES = [
  {
    title: 'Forgetting to square the turns ratio when referring impedance',
    body: 'Z_pri = Z_sec × a² — NOT Z_sec × a. For a 138/13.8 kV transformer (a = 10), a 0.5 Ω secondary impedance referred to the primary is 0.5 × 100 = 50 Ω, not 0.5 × 10 = 5 Ω. The error introduces a factor of a = 10 in the fault current calculation — a 10× error in a protection study is catastrophic. The physics: power must be conserved (V₁²/Z₁ = V₂²/Z₂), and since V₁/V₂ = a, we get Z₁/Z₂ = a².',
  },
  {
    title: 'Confusing the direction of referral (primary vs secondary side)',
    body: "When referring a secondary impedance to the primary: Z_pri = Z_sec × a² (multiply — it GROWS, because voltage is higher). When referring a primary impedance to the secondary: Z_sec = Z_pri / a² (divide — it SHRINKS). The direction of a² multiplication is opposite to what many expect. Check: if you're moving toward the higher voltage side, the impedance should get larger. If it's getting smaller, you've divided when you should have multiplied.",
  },
  {
    title: 'Using the line-to-line voltage instead of the winding voltage for turns ratio',
    body: "The turns ratio a = N₁/N₂ = V₁_winding/V₂_winding. For a wye winding, the winding voltage = V_LL/√3. For a delta winding, the winding voltage = V_LL. For a delta-wye transformer: a = V_LL_delta / (V_LL_wye/√3) = √3 × V_LL_delta/V_LL_wye. However, for nameplate voltage ratios, manufacturers always list line-to-line voltages, so for 138/13.8 kV the nameplate turns ratio a = 138/13.8 = 10 already accounts for the winding geometry. Always verify which convention is being used.",
  },
  {
    title: "Ignoring the 30° phase shift when computing differential relay currents",
    body: "A transformer differential relay (87T) compares the current flowing into the transformer on the HV side with the current flowing out on the LV side. For a Dyn11 transformer, the LV currents lead the HV currents by 30°. If the relay software does not compensate for this (or if delta-connected CTs are not used on the HV side), the 30° offset creates a false differential current that appears as a through-fault or internal fault. This causes relay trips during energization or external faults — both unacceptable.",
  },
  {
    title: 'Assuming delta zero-sequence blocking applies to the wye side',
    body: "A delta winding blocks zero-sequence current from flowing in the external line conductors on the DELTA side. The wye side (with a neutral) is NOT blocked — zero-sequence current can flow freely in the wye winding and its neutral connection. In a Dyn11 transformer, a single-line-to-ground fault on the LV (wye) side drives zero-sequence current through the LV neutral, through the delta winding (as a circulating current), and BACK to the fault — it does NOT appear in the HV (delta) line conductors. This is why the zero-sequence network shows the HV delta side as open.",
  },
];

const CommonMistakes = () => (
  <SectionWrapper number="5" title="Common Mistakes" id="common-mistakes">
    <p className="text-slate-600 dark:text-slate-400 text-sm mb-4">
      Transformer impedance referral and phase shift errors account for a significant fraction of protection misoperations in distribution systems.
    </p>
    <Accordion type="single" collapsible className="space-y-2" data-testid="mistakes-accordion">
      {MISTAKES.map((m, i) => (
        <AccordionItem
          key={i} value={`mistake-${i}`}
          className="border border-slate-200 dark:border-slate-700 rounded-lg px-4 bg-white dark:bg-slate-900"
        >
          <AccordionTrigger className="text-sm font-medium text-slate-800 dark:text-slate-200 hover:no-underline hover:text-teal-700 dark:hover:text-teal-400 py-3">
            <span className="flex items-center gap-2.5">
              <span className="text-red-500 text-xs font-bold w-4 flex-shrink-0">#{i + 1}</span>
              {m.title}
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed pb-3">{m.body}</p>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  </SectionWrapper>
);

// ─── Section 6: Worked Example ────────────────────────────────────────────────
const STEPS = [
  {
    label: 'State the given quantities',
    text: 'Transformer: 30 MVA, 138/13.8 kV, Dyn11, %Z = 7%. Required: turns ratio a, full-load currents on both sides (I₁, I₂), transformer leakage impedance in ohms on both sides, and verification that Z_sec × a² = Z_pri.',
    formula: null, result: null,
  },
  {
    label: 'Calculate the turns ratio a',
    text: 'The turns ratio is the ratio of rated HV to LV line-to-line voltages.',
    formula: "a = \\frac{V_1}{V_2} = \\frac{138\\,\\text{kV}}{13.8\\,\\text{kV}} = 10.0 \\qquad a^2 = 100",
    result: 'a = 10.0, a² = 100',
  },
  {
    label: 'Full-load secondary current I₂ (LV side)',
    text: 'Rearrange S = √3·V₂·I₂. Convert S to VA and V₂ to V.',
    formula: "I_2 = \\frac{S_{3\\phi}}{\\sqrt{3}\\cdot V_2} = \\frac{30{,}000{,}000}{1.7321 \\times 13{,}800} = \\frac{30{,}000{,}000}{23{,}902} = 1{,}255\\,\\text{A}",
    result: 'I₂ = 1,255 A (LV/secondary line current)',
  },
  {
    label: 'Full-load primary current I₁ (HV side)',
    text: 'I₁ = I₂/a. Verify independently: I₁ = S/(√3·V₁).',
    formula: "I_1 = \\frac{I_2}{a} = \\frac{1{,}255}{10} = 125.5\\,\\text{A} \\quad\\checkmark\\quad \\frac{S}{\\sqrt{3}\\cdot V_1} = \\frac{30{,}000{,}000}{1.7321 \\times 138{,}000} = 125.5\\,\\text{A}",
    result: 'I₁ = 125.5 A (HV/primary line current)',
  },
  {
    label: 'Transformer impedance Z_T on the primary (HV, 138 kV) side',
    text: 'First find the base impedance on the HV side, then apply %Z.',
    formula: "Z_{\\text{base,HV}} = \\frac{V^2_1}{S} = \\frac{(138{,}000)^2}{30{,}000{,}000} = \\frac{19.044 \\times 10^9}{30 \\times 10^6} = 634.8\\,\\Omega",
    result: 'Z_base,HV = 634.8 Ω',
  },
  {
    label: 'Apply %Z to find Z_T (HV side)',
    text: 'Z_T = (%Z/100) × Z_base,HV.',
    formula: "Z_T(\\text{HV}) = \\frac{7}{100} \\times 634.8 = 0.07 \\times 634.8 = 44.44\\,\\Omega",
    result: 'Z_T = 44.44 Ω (on primary/HV side)',
  },
  {
    label: 'Transformer impedance Z_T on the secondary (LV, 13.8 kV) side',
    text: 'Repeat using V₂ base impedance, then verify Z_sec × a² = Z_pri.',
    formula: "Z_{\\text{base,LV}} = \\frac{(13{,}800)^2}{30{,}000{,}000} = 6.348\\,\\Omega \\quad\\Rightarrow\\quad Z_T(\\text{LV}) = 0.07 \\times 6.348 = 0.4444\\,\\Omega",
    result: 'Z_T = 0.4444 Ω (on secondary/LV side)',
  },
  {
    label: 'Verify: Z_LV × a² must equal Z_HV',
    text: 'Referral check using a² = 100.',
    formula: "Z_T(\\text{LV}) \\times a^2 = 0.4444 \\times 100 = 44.44\\,\\Omega = Z_T(\\text{HV})\\;\\checkmark",
    result: 'Referral verified: both sides give 44.44 Ω and 0.4444 Ω respectively ✓',
  },
  {
    label: 'Phase shift (Dyn11) — write the Va phasors',
    text: 'Taking HV Va as reference (∠0°). LV Va′ leads by +30° (Dyn11). Magnitudes: V_LN,HV = 138/√3, V_LN,LV = 13.8/√3.',
    formula: "\\mathbf{V}_{a,\\text{HV}} = 79.67\\angle 0^\\circ\\,\\text{kV} \\qquad \\mathbf{V}_{a,\\text{LV}} = 7.967\\angle{+30^\\circ}\\,\\text{kV}\\quad(\\text{Dyn11})",
    result: 'LV Va′ = 7.967 kV ∠+30° for Dyn11',
  },
];

const WorkedExample = () => (
  <SectionWrapper number="6" title="Step-by-Step Worked Example" id="worked-example">
    <div className="mb-5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg p-5">
      <p className="text-xs font-semibold text-slate-500 dark:text-slate-500 uppercase tracking-widest mb-3">Problem Statement</p>
      <p className="text-slate-700 dark:text-slate-300 text-sm leading-relaxed">
        A substation transformer is rated <strong>30 MVA, 138/13.8 kV, Dyn11, %Z = 7%</strong>.
        Find: (1) turns ratio a, (2) full-load line currents I₁ (HV) and I₂ (LV), (3) transformer leakage
        impedance Z_T in ohms on both the HV and LV sides, (4) verify Z_LV × a² = Z_HV,
        (5) write the Va phasor on both sides showing the 30° Dyn11 shift.
      </p>
    </div>
    <div className="space-y-3" data-testid="worked-example-steps">
      {STEPS.map((step, i) => (
        <div key={i} className="step-card border border-slate-200 dark:border-slate-700 rounded-lg p-4 bg-white dark:bg-slate-900">
          <div className="flex items-start gap-3">
            <div className="section-badge flex-shrink-0 text-xs">{i + 1}</div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-slate-900 dark:text-white text-sm mb-2 font-heading">{step.label}</p>
              {step.text && <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed mb-2">{step.text}</p>}
              {step.formula && (
                <div className="overflow-x-auto bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-md p-3 my-2">
                  <BlockMath math={step.formula} />
                </div>
              )}
              {step.result && (
                <div className="inline-flex items-center gap-2 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-700 rounded-md px-3 py-1.5 mt-1">
                  <ChevronRight className="h-3 w-3 text-teal-600" />
                  <span className="text-xs font-mono font-semibold text-teal-800 dark:text-teal-300">{step.result}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  </SectionWrapper>
);

// ─── Section 7: Calculators ───────────────────────────────────────────────────
const CalculatorsSection = () => (
  <SectionWrapper number="7" title="Interactive Calculators" id="calculators" alt>
    <Tabs defaultValue="turns" className="w-full" data-testid="calculators-tabs">
      <TabsList className="mb-6 bg-slate-100 dark:bg-slate-800">
        <TabsTrigger value="turns"   data-testid="tab-turns">Turns Ratio</TabsTrigger>
        <TabsTrigger value="impedan" data-testid="tab-impedance">Impedance Referral</TabsTrigger>
        <TabsTrigger value="phase"   data-testid="tab-phase">Phase Shift</TabsTrigger>
      </TabsList>

      <TabsContent value="turns">
        <div className="mb-4">
          <h3 className="font-semibold text-slate-900 dark:text-white font-heading mb-1">Turns Ratio Calculator</h3>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Enter the HV/LV rated voltages and secondary line current. Computes turns ratio a, a², primary current I₁,
            and apparent power S with voltage and current comparison charts.
          </p>
        </div>
        <TransformerTurnsRatioCalc />
      </TabsContent>

      <TabsContent value="impedan">
        <div className="mb-4">
          <h3 className="font-semibold text-slate-900 dark:text-white font-heading mb-1">Impedance Referral Calculator</h3>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Select the referral direction (secondary→primary or primary→secondary), enter the transformer voltages and
            the impedance to be referred. Shows the full formula substitution with the critical a² factor highlighted.
          </p>
        </div>
        <ImpedanceReferralCalc />
      </TabsContent>

      <TabsContent value="phase">
        <div className="mb-4">
          <h3 className="font-semibold text-slate-900 dark:text-white font-heading mb-1">Delta-Wye Phase Shift Calculator</h3>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Select the vector group (Dyn11, Dyn1, YNyn0), enter HV and LV voltages. Shows a phasor diagram with
            both HV (solid) and LV (dashed) phasors, the angular offset, and a correspondence table.
          </p>
        </div>
        <DeltaWyePhaseShiftCalc />
      </TabsContent>
    </Tabs>
  </SectionWrapper>
);

// ─── Section 8: Self-Check Quiz ───────────────────────────────────────────────
const QUESTIONS = [
  {
    q: 'A 138/13.8 kV transformer. What is the turns ratio a?',
    options: [
      { k: 'a', label: '1.0' },
      { k: 'b', label: '10.0' },
      { k: 'c', label: '100.0' },
      { k: 'd', label: '0.1' },
    ],
    correct: 'b',
    explanation: 'a = V1/V2 = 138/13.8 = 10.0. The HV winding has 10× more turns (and 10× less current) than the LV winding.',
  },
  {
    q: 'A fault impedance of 0.5 Ω on the 13.8 kV secondary side. Referred to the 138 kV primary, what is it?',
    options: [
      { k: 'a', label: '5 Ω' },
      { k: 'b', label: '0.005 Ω' },
      { k: 'c', label: '50 Ω' },
      { k: 'd', label: '500 Ω' },
    ],
    correct: 'c',
    explanation: 'Z_pri = Z_sec × a² = 0.5 × 10² = 0.5 × 100 = 50 Ω. Note: the answer is NOT 0.5 × 10 = 5 Ω — the turns ratio must be squared.',
  },
  {
    q: 'In a Dyn11 transformer, the LV secondary voltage relative to the HV primary voltage is:',
    options: [
      { k: 'a', label: 'In phase (0° shift)' },
      { k: 'b', label: 'Leads by +30°' },
      { k: 'c', label: 'Lags by −30°' },
      { k: 'd', label: 'Leads by +60°' },
    ],
    correct: 'b',
    explanation: "Dyn11: the '11' means 11 clock positions × 30° = 330° lag = equivalent to +30° lead. LV phasors lead HV by 30° in Dyn11. This must be compensated in differential relay settings.",
  },
  {
    q: 'A delta-connected winding blocks zero-sequence current from:',
    options: [
      { k: 'a', label: 'Circulating inside the delta loop' },
      { k: 'b', label: 'Flowing in the wye winding' },
      { k: 'c', label: 'Flowing in the external line conductors connected to the delta terminals' },
      { k: 'd', label: 'All current components' },
    ],
    correct: 'c',
    explanation: 'Zero-sequence current CAN circulate inside the delta loop (driven by MMF from the other winding), but it CANNOT flow in the external line conductors connected to delta terminals. KCL at each delta node prevents three equal in-phase currents from entering or leaving.',
  },
  {
    q: 'A 30 MVA, 138 kV, 7%Z transformer. What is the leakage impedance on the HV side in ohms?',
    options: [
      { k: 'a', label: '210 Ω' },
      { k: 'b', label: '634.8 Ω' },
      { k: 'c', label: '44.4 Ω' },
      { k: 'd', label: '4.44 Ω' },
    ],
    correct: 'c',
    explanation: 'Z_base = V²/S = (138,000)²/30,000,000 = 634.8 Ω. Z_T = 0.07 × 634.8 = 44.44 Ω ≈ 44.4 Ω. The base impedance (634.8 Ω) is the full-rated impedance; 7% of that is the leakage.',
  },
];

const SelfCheckQuiz = () => {
  const [selected, setSelected] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const score = submitted ? QUESTIONS.filter((q, i) => selected[i] === q.correct).length : 0;
  const reset = () => { setSelected({}); setSubmitted(false); };

  return (
    <SectionWrapper number="8" title="Self-Check Quiz" id="quiz">
      <p className="text-slate-600 dark:text-slate-400 text-sm mb-6">
        Five questions covering turns ratio, impedance referral, phase shift, and zero-sequence blocking.
      </p>
      <div className="space-y-6" data-testid="quiz-section">
        {QUESTIONS.map((q, qi) => (
          <div key={qi} data-testid={`quiz-question-${qi}`} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg p-5">
            <p className="font-medium text-slate-900 dark:text-white text-sm mb-3">
              <span className="text-teal-700 dark:text-teal-400 font-mono mr-2">Q{qi + 1}.</span>
              {q.q}
            </p>
            <div className="space-y-2">
              {q.options.map(opt => {
                const isSel     = selected[qi] === opt.k;
                const isCorrect = opt.k === q.correct;
                let cls = 'border rounded-md px-3 py-2 text-sm cursor-pointer transition-colors flex items-center gap-2 ';
                if (!submitted) {
                  cls += isSel
                    ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20 text-teal-900 dark:text-teal-100'
                    : 'border-slate-200 dark:border-slate-700 hover:border-teal-400 text-slate-700 dark:text-slate-300';
                } else {
                  if (isCorrect) cls += 'border-green-500 bg-green-50 dark:bg-green-900/20 text-green-900 dark:text-green-100';
                  else if (isSel) cls += 'border-red-400 bg-red-50 dark:bg-red-900/20 text-red-900 dark:text-red-100';
                  else cls += 'border-slate-100 dark:border-slate-800 text-slate-400 dark:text-slate-600 opacity-50';
                }
                return (
                  <button key={opt.k} data-testid={`quiz-option-${qi}-${opt.k}`} className={cls}
                    onClick={() => !submitted && setSelected(prev => ({ ...prev, [qi]: opt.k }))}
                    disabled={submitted}>
                    {submitted && isCorrect  && <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />}
                    {submitted && isSel && !isCorrect && <XCircle className="h-4 w-4 text-red-500 flex-shrink-0" />}
                    <span className="font-mono text-xs font-bold mr-1">{opt.k.toUpperCase()}.</span>
                    {opt.label}
                  </button>
                );
              })}
            </div>
            {submitted && (
              <div className="mt-3 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-md p-3">
                <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                  <span className="font-semibold text-teal-700 dark:text-teal-400">Explanation: </span>
                  {q.explanation}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="flex items-center gap-4 mt-6">
        {!submitted ? (
          <button data-testid="quiz-submit"
            onClick={() => Object.keys(selected).length > 0 && setSubmitted(true)}
            disabled={Object.keys(selected).length === 0}
            className="px-5 py-2 bg-teal-600 hover:bg-teal-500 disabled:bg-slate-300 dark:disabled:bg-slate-700 disabled:cursor-not-allowed text-white font-medium rounded-md transition-colors text-sm">
            Submit Answers ({Object.keys(selected).length}/{QUESTIONS.length} answered)
          </button>
        ) : (
          <>
            <div data-testid="quiz-score" className={`px-4 py-2 rounded-md text-sm font-semibold ${
              score === QUESTIONS.length ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300 border border-green-200 dark:border-green-800'
              : score >= 3 ? 'bg-amber-100 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800'
              : 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300 border border-red-200 dark:border-red-800'
            }`}>
              Score: {score}/{QUESTIONS.length}
              {score === QUESTIONS.length && ' — Perfect!'}
              {score >= 3 && score < QUESTIONS.length && ' — Good work'}
              {score < 3 && ' — Review the formulas above and try again'}
            </div>
            <button data-testid="quiz-reset" onClick={reset} className="text-sm text-slate-600 dark:text-slate-400 hover:text-teal-700 dark:hover:text-teal-400 underline transition-colors">
              Try again
            </button>
          </>
        )}
      </div>
    </SectionWrapper>
  );
};

// ─── Section 9: Connected Topics + Coming Next ────────────────────────────────
const ConnectedTopics = () => (
  <SectionWrapper number="9" title="Connected Topics" id="connected-topics" alt>
    <p className="text-slate-600 dark:text-slate-400 text-sm mb-5">
      Transformer Systems is the second link in the learning chain. Every module after this uses the turns ratio,
      referred impedance, phase shift, and zero-sequence blocking concepts taught here.
    </p>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-500 mb-3">Prerequisites</p>
        <Link to="/foundation" className="flex items-center gap-3 p-3 border border-teal-100 dark:border-teal-900/40 rounded-lg hover:border-teal-400 dark:hover:border-teal-700 transition-colors bg-teal-50/50 dark:bg-teal-900/10">
          <ArrowRight className="h-4 w-4 text-teal-600 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-slate-900 dark:text-white font-heading">M01 Foundation</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">Phasors, V_LL = √3·V_LN, S = √3·V·I used throughout this module</p>
          </div>
        </Link>
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-500 mb-3">Next Steps</p>
        <div className="space-y-2">
          {[
            { to: '/per-unit', label: 'M03 Per-Unit Analysis', desc: 'Eliminates a² referral by normalizing all impedances to one base' },
            { to: '/symmetrical-components', label: 'M04 Symmetrical Components', desc: 'Uses delta zero-sequence blocking in sequence networks' },
          ].map(({ to, label, desc }) => (
            <Link key={to} to={to} className="flex items-center gap-3 p-3 border border-teal-100 dark:border-teal-900/40 rounded-lg hover:border-teal-400 dark:hover:border-teal-700 transition-colors bg-teal-50/50 dark:bg-teal-900/10">
              <ArrowRight className="h-4 w-4 text-teal-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-slate-900 dark:text-white font-heading">{label}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">{desc}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>

    {/* ── Coming Next Card ── */}
    <div className="bg-gradient-to-br from-indigo-50 to-teal-50 dark:from-indigo-950/40 dark:to-teal-950/40 border border-indigo-200 dark:border-indigo-800 rounded-xl p-5">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-xs font-mono font-semibold text-indigo-700 dark:text-indigo-400 bg-indigo-100 dark:bg-indigo-900/40 border border-indigo-200 dark:border-indigo-800 px-2 py-0.5 rounded">M03</span>
        <p className="text-sm font-semibold text-slate-900 dark:text-white font-heading">Coming Next: Per-Unit Analysis</p>
      </div>
      <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed mb-3">
        In this module, you worked through transformer impedance referral using a² — multiplying or dividing
        every impedance every time it crosses a voltage boundary. <strong className="text-slate-800 dark:text-slate-200">Per-unit analysis was invented to eliminate this entirely.</strong>
      </p>
      <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed mb-3">
        By choosing a system-wide MVA base and voltage bases that match the transformer turns ratio at each level,
        per-unit normalization makes transformer impedance identical on both sides (Z_T_pu = %Z/100 always),
        and the turns ratio effectively disappears from the network equations. The a² factor you just mastered
        is exactly what per-unit absorbs.
      </p>
      <Link to="/per-unit" data-testid="coming-next-m03-link"
        className="inline-flex items-center gap-2 text-sm font-semibold text-indigo-700 dark:text-indigo-400 hover:text-teal-700 dark:hover:text-teal-400 transition-colors">
        Go to M03 Per-Unit Analysis
        <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  </SectionWrapper>
);

// ─── Main Module Page ──────────────────────────────────────────────────────────
const TransformerModule = () => (
  <div data-testid="transformer-module">
    {/* Module Header */}
    <div className="relative bg-gradient-to-br from-[#0B172A] via-indigo-950 to-purple-900 text-white px-4 sm:px-6 lg:px-8 py-12 overflow-hidden">
      <div className="relative max-w-4xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-1.5 text-slate-400 hover:text-white text-sm mb-5 transition-colors">
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to Curriculum Map
        </Link>
        <div className="flex items-center gap-3 mb-3">
          <span className="text-xs font-mono font-semibold text-purple-400 bg-purple-400/10 border border-purple-400/20 px-2 py-1 rounded">M02</span>
          <span className="text-xs font-bold bg-teal-500 text-white px-2 py-0.5 rounded-full">LIVE</span>
          <span className="text-xs text-slate-400 uppercase tracking-widest">Prerequisite: M01 Foundation</span>
        </div>
        <h1 className="text-4xl sm:text-5xl font-semibold font-heading mb-3 leading-tight">Transformer Systems</h1>
        <p className="text-slate-300 text-lg mb-4">Delta-Wye Connections, Vector Groups, and Impedance Referral</p>
        <p className="text-slate-400 text-sm max-w-2xl">
          Master transformer turns ratio, the a² impedance referral law, delta zero-sequence blocking,
          and the 30° phase shift in Dyn11 connections — essential for every protection and fault study.
        </p>
      </div>
    </div>

    {/* Section Nav */}
    <div className="sticky top-14 z-20 bg-white/95 dark:bg-slate-950/95 backdrop-blur-sm border-b border-slate-200 dark:border-slate-800 overflow-x-hidden">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
        <div className="flex gap-0 py-0" style={{ scrollbarWidth: 'none' }}>
          {[
            { n: 1, label: 'Why',       id: 'why-it-matters' },
            { n: 2, label: 'Visual',    id: 'concept-visual' },
            { n: 3, label: 'Theory',    id: 'core-theory' },
            { n: 4, label: 'Formulas',  id: 'formulas' },
            { n: 5, label: 'Mistakes',  id: 'common-mistakes' },
            { n: 6, label: 'Example',   id: 'worked-example' },
            { n: 7, label: 'Calculators', id: 'calculators' },
            { n: 8, label: 'Quiz',      id: 'quiz' },
            { n: 9, label: 'Connections', id: 'connected-topics' },
          ].map(({ n, label, id }) => (
            <a key={n} href={`#${id}`}
              className="px-3 py-3 text-xs font-medium text-slate-500 dark:text-slate-500 hover:text-teal-700 dark:hover:text-teal-400 whitespace-nowrap border-b-2 border-transparent hover:border-teal-500 transition-colors flex-shrink-0">
              <span className="text-teal-600 dark:text-teal-500 mr-1 font-mono">{n}.</span>{label}
            </a>
          ))}
        </div>
      </div>
    </div>

    {/* All 9 Sections */}
    <WhyItMatters />
    <ConceptVisual />
    <CoreTheory />
    <FormulaSheet />
    <CommonMistakes />
    <WorkedExample />
    <CalculatorsSection />
    <SelfCheckQuiz />
    <ConnectedTopics />

    {/* Bottom nav */}
    <div className="border-t border-slate-200 dark:border-slate-800 px-4 sm:px-6 lg:px-8 py-6">
      <div className="max-w-4xl mx-auto flex items-center justify-between">
        <Link to="/foundation" data-testid="prev-module-btn"
          className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 hover:text-teal-700 dark:hover:text-teal-400 transition-colors">
          <ArrowLeft className="h-4 w-4" />
          M01 Foundation
        </Link>
        <Link to="/per-unit" data-testid="next-module-btn"
          className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 hover:text-teal-700 dark:hover:text-teal-400 transition-colors">
          M03 Per-Unit Analysis
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  </div>
);

export default TransformerModule;
